import { Grid, Select, useConfirmation } from '@approvalmax/ui/src/components';
import { memo, useCallback } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { useCompanyBetaFeatures, useCompanyIdBasedOnUrl } from 'shared/hooks';

import {
    countries,
    countriesForConfirmationOfPayee,
    currencies,
    currenciesForConfirmationOfPayee,
} from '../../EditCounterpartyPaymentDetailsPopupContent.constants';
import { FormValues } from '../../EditCounterpartyPaymentDetailsPopupContent.types';
import { messages } from './FirstStep.messages';
import { FirstStepProps } from './FirstStep.types';

/**
 * NOTE: We don't use Form.Part in this component
 * because we need to change values only after user confirmation(or don't apply changes if user cancels).
 * We don't have such ability with react-hook-form and our Controller version of Select component.
 * That's why we use non-Controller version of Select component with onChange handler
 * where we set value in form from context manualy.
 */
export const FirstStep = memo<FirstStepProps>((props) => {
    const { disabled } = props;

    const form = useFormContext<FormValues>();
    const { companyId } = useCompanyIdBasedOnUrl();
    const { isWalletsUK, isConfirmationOfPayee } = useCompanyBetaFeatures(companyId);

    const onlyConfirmationOfPayee = isConfirmationOfPayee && !isWalletsUK;

    const [payeeBankCountry, payerBankCountry, currencyCode] = form.watch([
        'payeeBankCountry',
        'payerBankCountry',
        'currencyCode',
    ]);

    const { confirmation } = useConfirmation({
        confirmTitle: messages.confirmChanges,
        confirmMessage: messages.confirmChangesDescription,
        confirmButtonMessage: messages.confirm,
        // to prevent select opening after confirmation
        returnFocus: false,
    });

    const createOnChangeHandler = useCallback(
        (fieldName: FieldPath<FormValues>) => (value: string) => {
            const values = form.getValues();

            const hasValuesOnSecondStep = values.paymentMethod || values.legalEntity?.legalEntityType;

            if (hasValuesOnSecondStep) {
                confirmation()
                    .then(() => {
                        form.setValue(fieldName, value);
                    })
                    .catch(() => {});
            } else {
                form.setValue(fieldName, value);
            }
        },
        [form, confirmation]
    );

    return (
        <Grid gridTemplateColumns='repeat(2, 1fr)' gap={16}>
            <Select
                name='payeeBankCountry'
                label={messages.payeeBankCountry}
                disabled={disabled}
                placeholder={messages.selectPlaceholder}
                items={onlyConfirmationOfPayee ? countriesForConfirmationOfPayee : countries}
                required
                clearable={false}
                autocomplete
                value={payeeBankCountry}
                onChange={createOnChangeHandler('payeeBankCountry')}
            />

            <Select
                name='currencyCode'
                label={messages.accountCurrency}
                disabled={disabled}
                placeholder={messages.selectPlaceholder}
                items={onlyConfirmationOfPayee ? currenciesForConfirmationOfPayee : currencies}
                required
                clearable={false}
                autocomplete
                value={currencyCode}
                onChange={createOnChangeHandler('currencyCode')}
            />

            <Select
                name='payerBankCountry'
                label={messages.payerBankCountry}
                disabled={disabled}
                placeholder={messages.selectPlaceholder}
                items={onlyConfirmationOfPayee ? countriesForConfirmationOfPayee : countries}
                required
                clearable={false}
                autocomplete
                value={payerBankCountry}
                onChange={createOnChangeHandler('payerBankCountry')}
            />
        </Grid>
    );
});

FirstStep.displayName = 'FirstStep';
